import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, RefreshCw, Send, MessageSquare, Mail, AlertTriangle } from "lucide-react";
import { adminClient } from "./api";

function ChannelRow({ id, icon: Icon, label, to, live, statusLabel, hint, onTest, testing }) {
  return (
    <div data-testid={`${id}-channel-row`} className="rounded-md border border-zinc-200 p-3 bg-zinc-50/60">
      <div className="flex items-center gap-2">
        <Icon className="w-3.5 h-3.5 text-[#0C2E2B]" />
        <span className="text-xs font-bold text-[#0C2E2B]">{label}</span>
        <Badge data-testid={`${id}-status-badge`} className={`ml-auto text-[10px] ${live ? "bg-emerald-600 hover:bg-emerald-700" : "bg-zinc-400 hover:bg-zinc-500"}`}>
          {statusLabel}
        </Badge>
      </div>
      <div className="mt-1.5 text-[11px] text-zinc-500 flex justify-between gap-2">
        <span>to</span>
        <span data-testid={`${id}-recipient`} className="font-mono-data font-bold text-[#0C2E2B] truncate">{to || "—"}</span>
      </div>
      {hint && (
        <div data-testid={`${id}-hint`} className="mt-2 flex items-start gap-1.5 text-[10px] text-amber-800 bg-amber-50 border-l-2 border-amber-500 px-2 py-1.5 rounded-r">
          <AlertTriangle className="w-3 h-3 mt-px shrink-0" /> <span>{hint}</span>
        </div>
      )}
      <Button
        data-testid={`${id}-test-btn`}
        variant="outline"
        size="sm"
        onClick={onTest}
        disabled={testing || !to}
        className="mt-2 w-full rounded-md text-[11px] h-8"
      >
        <Send className="w-3 h-3 mr-1" /> {testing ? "Sending…" : `Send test ${label}`}
      </Button>
    </div>
  );
}

export default function AlertSettingsCard() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState("");
  const [smsDelivery, setSmsDelivery] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminClient.get("/admin/alert-settings");
      setSettings(data);
    } catch { setSettings(null); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendTest = async (channel) => {
    setTesting(channel);
    try {
      const { data } = await adminClient.post("/admin/alerts/test", { channel });
      const r = data.result || {};
      if (channel === "sms") setSmsDelivery(r.status === "sent" ? null : r.detail || null);
      if (r.status === "sent") toast.success(`Test ${channel} sent — check your ${channel === "sms" ? "phone" : "inbox"}`);
      else toast.error(`${channel.toUpperCase()} not delivered: ${r.detail || r.reason || "unknown error"}`, { duration: 12000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not send test ${channel}`);
    } finally { setTesting(""); }
  };

  const sms = settings?.sms || {};
  const smsLive = !!settings?.sms_configured;
  const emailLive = !!settings?.email_configured;
  const gateway = settings?.sms_gateway;
  const smsStatus = gateway && smsLive && !sms.auth_ok ? "LIVE · EMAIL GATEWAY" : !sms.configured ? "DISABLED" : !sms.auth_ok ? "AUTH FAILED" : !sms.from_number ? "NO NUMBER" : "LIVE";
  const smsHint = gateway && !sms.auth_ok
    ? `Twilio is suspended/unauthorized (${sms.reason || "auth failed"}); your alerts are being delivered through your carrier's free email-to-text gateway (${gateway}). Customer texts still need Twilio reinstated.`
    : !sms.configured
    ? "Add TWILIO_ACCOUNT_SID and TWILIO_AUTH_TOKEN to backend/.env."
    : !sms.auth_ok
      ? `Twilio rejected the credentials (${sms.reason || "auth failed"}). Re-copy the 32-character Auth Token from console.twilio.com.`
      : !sms.from_number
        ? "Buy or add a phone number in Twilio, then set TWILIO_FROM_NUMBER."
        : smsDelivery || "";
  const emailDomain = settings?.email_domain;
  const emailHint = !emailLive
    ? "Add RESEND_API_KEY to backend/.env."
    : emailDomain?.status === "test_address"
      ? `Sending from Resend's shared test address (${settings.sender_email}). Verify your domain at resend.com/domains to send from your own address.`
      : emailDomain && !emailDomain.verified && emailDomain.status !== "unknown"
        ? `Resend reports ${emailDomain.domain} is ${emailDomain.status.replace(/_/g, " ")}. Finish DNS setup at resend.com/domains.`
        : "";

  return (
    <Card data-testid="alert-settings-card" className="p-5 border-2 border-zinc-200">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-[#0C2E2B]" />
          <h3 className="font-display font-black text-[#0C2E2B]">Alerts</h3>
        </div>
        <button data-testid="alerts-refresh-btn" onClick={load} className="text-zinc-400 hover:text-[#0C2E2B]">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {loading && !settings ? (
        <div className="text-xs text-zinc-500 mt-3">Checking providers…</div>
      ) : !settings ? (
        <div className="text-xs text-red-600 mt-3">Could not load alert settings</div>
      ) : (
        <div className="mt-3 space-y-2">
          <ChannelRow
            id="sms" icon={MessageSquare} label="SMS" to={settings.alert_phone}
            live={smsLive} statusLabel={smsStatus} hint={smsHint}
            onTest={() => sendTest("sms")} testing={testing === "sms"}
          />
          <ChannelRow
            id="email" icon={Mail} label="Email" to={settings.alert_email}
            live={emailLive} statusLabel={emailLive ? "LIVE" : "DISABLED"} hint={emailHint}
            onTest={() => sendTest("email")} testing={testing === "email"}
          />
          <p className="text-[10px] text-zinc-500 leading-snug pt-1">
            Every booking, order and inquiry is saved in the feed below even if a provider is down.
          </p>
        </div>
      )}
    </Card>
  );
}
